import React, { useState } from "react";
import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  DropdownFilter,
  LeadsRadialPieChart,
  metricsToRadialSlices,
  TABLE_MIN_HEIGHT,
  TableFooter,
  PageAnalyticsChart,
} from "../../common";
import {
  useLandingPagesQuery,
  useMetricsChartQuery,
  useMetricsQuery,
  useRowTrendQuery,
} from "src/query/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { formatDurationWithLabel, safeDivide } from "../../utils/format";
import { isInteractive } from "../../constants";
import { ArrangeOption } from "../../common/DropdownFilter";
import { ReportRequest } from "src/types/experiences/leadsAndReports";
import styles from "../../common/common.module.scss";

interface GeographicAnalyticsProps {
  storyboardId: string;
  storyboardType?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "location", header: "Location" },
  { key: "views", header: "Views", align: "right" },
  { key: "visitors", header: "Visitors", align: "right" },
  { key: "avg", header: "Avg. Time Spent", align: "right" },
  { key: "total", header: "Total Time Spent", align: "right" },
  { key: "page", header: "Page Analytics", align: "right-fill" },
];

const CountryRow: React.FC<{
  row: any;
  variant: "interactive" | "landing";
  request: ReportRequest;
  cacheKey: string;
  typeReady: boolean;
  pages: any[];
}> = ({ row, variant, request, cacheKey, typeReady, pages }) => {
  const { data: trend } = useRowTrendQuery(
    variant,
    "country",
    row.metricType ?? null,
    { ...request, country: row.metricType },
    cacheKey,
    Boolean(row.metricType) && typeReady,
    pages,
  );

  return (
    <>
      <Cell headline={row.metricType} />
      <Cell headline={row.views} />
      <Cell headline={row.visitors} />
      <Cell headline={formatDurationWithLabel(safeDivide(row.timeSpent, row.views)).value} />
      <Cell headline={formatDurationWithLabel(row.timeSpent).value} />
      <div>
        {trend && trend.length > 0 ? (
          <PageAnalyticsChart data={trend} valueKey="views" />
        ) : (
          "-"
        )}
      </div>
    </>
  );
};

export const GeographicAnalytics: React.FC<GeographicAnalyticsProps> = ({ storyboardId, storyboardType }) => {
  const { request, key, page, setPage, chart, setChart, enabled } = useTableState(storyboardId);
  const typeReady = storyboardType !== undefined;
  const variant = isInteractive(storyboardType) ? "interactive" : "landing";
  const [viewOption, setViewOption] = useState<ArrangeOption>({
    label: "Views",
    value: "views",
    name: "metricType",
  });

  const { data, isLoading } = useMetricsQuery("country", request, key, page, enabled);
  const { data: chartData, isLoading: isChartLoading } = useMetricsChartQuery(
    "country",
    request,
    key,
    enabled,
  );
  const { data: pages = [] } = useLandingPagesQuery(storyboardId, variant === "landing");
  const rows = data?.records ?? [];
  const pieData = chartData?.records ?? [];

  const renderChart = () => {
    switch (chart) {
      case "pie_chart_view":
        return (
          <div className={styles.chartFull}>
            <LeadsRadialPieChart
              data={metricsToRadialSlices(pieData, viewOption)}
              measureLabel={viewOption.label}
              height={280}
              loading={isChartLoading}
            />
          </div>
        );
      case "table_view":
        return (
          <AnalyticsTable
            columns={COLUMNS}
            rows={rows}
            loading={isLoading}
            renderRow={(row: any) => (
              <CountryRow row={row} variant={variant} request={request} cacheKey={key} typeReady={typeReady} pages={pages} />
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Geography Analytics"
      count={data?.totalRecordsCount ?? rows.length}
      currentChart={chart}
      disableView={["graph_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.full}
      action={
        chart === "pie_chart_view" ? (
          <DropdownFilter
            arrangeOption={viewOption}
            handleArrange={(o) => setViewOption((s) => ({ ...s, ...o }))}
            disableOption={[]}
          />
        ) : undefined
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Geography"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
